import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../components/ui/table';
import { useToast } from '../components/ui/use-toast';
import { Plus, Pencil, Save, ArrowLeft, Loader2 } from 'lucide-react';
import { useRestaurant } from '../context/RestaurantContext';
import {
  listTaxLabels, listTaxGroups, createTaxGroup, updateTaxGroup,
} from '../services/taxRateService';

/** Tax Groups bundle taxes so they apply together, edited as details plus a checkbox list of the rates. */
export default function TaxGroupsManagement() {
  const { toast } = useToast();
  const { restaurantId } = useRestaurant();

  const [groups, setGroups] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} | group
  const [form, setForm] = useState({ name: '', description: '', labelIds: [] });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const [g, l] = await Promise.all([
        listTaxGroups(restaurantId),
        listTaxLabels(restaurantId),
      ]);
      setGroups(g);
      setLabels(l.filter((x) => x.is_active));
    } catch {
      toast({ title: 'Error', description: 'Could not load tax groups', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, toast]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: '', description: '', labelIds: [] }); setEditing({}); };
  const openEdit = (g) => {
    setForm({
      name: g.name,
      description: g.description || '',
      labelIds: (g.sub_labels || []).map((s) => s.id),
    });
    setEditing(g);
  };

  const toggleLabel = (id) =>
    setForm((f) => ({
      ...f,
      labelIds: f.labelIds.includes(id)
        ? f.labelIds.filter((x) => x !== id)
        : [...f.labelIds, id],
    }));

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Validation', description: 'Name is required', variant: 'destructive' });
      return;
    }
    if (form.labelIds.length < 1) {
      toast({ title: 'Validation', description: 'Select at least one tax', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        // Order is application order; each tax's own compound flag governs the bill, so there is no per-group input.
        labels: form.labelIds.map((id) => ({
          id, is_compound: !!labels.find((l) => l.id === id)?.is_compound,
        })),
      };
      if (editing?.id) await updateTaxGroup(restaurantId, editing.id, payload);
      else await createTaxGroup(restaurantId, payload);
      toast({ title: editing?.id ? 'Tax group updated' : 'Tax group created' });
      setEditing(null);
      load();
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.error || e.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (editing !== null) {
    return (
      <div className="p-6 w-full">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setEditing(null)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">{editing?.id ? 'Edit Tax Group' : 'New Tax Group'}</h1>
          </div>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            Save
          </Button>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-6">
            <h2 className="font-semibold mb-4">Tax Group Details</h2>
            <div className="max-w-md">
              <Label>Name <span className="text-red-500">*</span></Label>
              <Input className="mt-1" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <Textarea className="mt-4" rows={3} placeholder="Description" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h2 className="font-semibold mb-4">Select Taxes</h2>
            {labels.length === 0 ? (
              <p className="text-sm text-gray-400">
                No active tax rates — create them under Settings → Tax.
              </p>
            ) : labels.map((l) => (
              <label key={l.id} className="flex items-center gap-2 text-sm py-1">
                <input type="checkbox" checked={form.labelIds.includes(l.id)}
                  onChange={() => toggleLabel(l.id)} />
                {l.name}
                {l.current_rate != null && (
                  <span className="text-gray-400">
                    ({l.rate_type === 'FLAT' ? `Rs ${l.current_rate}` : `${l.current_rate}%`}
                    {l.is_compound ? ', compound' : ''})
                  </span>
                )}
              </label>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Tax Groups</h1>
        <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-1.5" /> New Tax Group
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Taxes</TableHead>
              <TableHead className="text-right">Effective Rate</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-400">Loading…</TableCell></TableRow>
            ) : groups.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-10 text-gray-400">
                No tax groups yet — click <strong>New Tax Group</strong> to add one.
              </TableCell></TableRow>
            ) : groups.map((g) => (
              <TableRow key={g.id}>
                <TableCell className="font-medium">
                  {g.name}
                  {g.description && <div className="text-xs text-gray-400">{g.description}</div>}
                </TableCell>
                <TableCell className="text-sm text-gray-600">
                  {(g.sub_labels || []).map((s) => s.name).join(' + ') || '—'}
                </TableCell>
                <TableCell className="text-right">{Number(g.effective_rate ?? 0).toFixed(4)}%</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full ${
                    g.is_active ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {g.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(g)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
